/**
 * Nhật ký thao tác — ai đã làm gì, lúc nào, từ IP nào.
 * Đây là chỗ tra khi có tranh cãi "đèn tắt đêm qua là do ai bấm".
 *
 * ═══════════════════════════════════════════════════════════════════════════════════════
 * ➕ `v4` `S6` — TRANG NÀY **AI CŨNG VÀO ĐƯỢC**, và phạm vi co lại theo quyền:
 *
 *     co tick `tao_tai_khoan`  ->  ca nhat ky (nhu cu)
 *     khong co                 ->  CHI viec cua chinh minh   (`?cuaToi=1`, may chu ep userId)
 *
 * ⛔ VÀ TRANG **KHÔNG TỰ LỌC** — nó chỉ NÓI RA mình đang xin phạm vi nào; máy chủ ép. Nếu web
 *   lọc lấy thì một bản web cũ hơn, hay một `curl`, vẫn đọc được cả bảng: *một bộ lọc ở trình
 *   duyệt không phải một cửa.* Ở đây web chỉ làm đúng một việc thật: **nói cho người dùng biết
 *   họ đang nhìn tập nào**, vì một danh sách 3 dòng không tự khai là "3 dòng của bạn" hay "cả
 *   hệ thống chỉ có 3 dòng".
 * ═══════════════════════════════════════════════════════════════════════════════════════
 */

import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ClipboardList, RefreshCw, Search } from 'lucide-react';
import { useAudit, useAuditDem } from '../lib/queries';
import { useQuyen } from '../lib/auth';
import { BangKhoa } from '../components/Khoa';
import { NHAN_KHOA } from '../lib/quyen';
import { aiDaLam } from '../lib/nguonthaotac';
import {
  Badge, Button, Card, EmptyState, ErrorBox, SectionTitle, Spinner,
} from '../components/ui';
import { fmtAgo, fmtDateTime } from '../lib/format';
import type { AuditRow } from '../lib/types';

/** Dịch mã hành động của server sang tiếng Việt. Mã lạ thì hiện nguyên văn. */
const ACTION_LABEL: Record<string, string> = {
  LOGIN: 'Đăng nhập',
  LOGOUT: 'Đăng xuất',
  CHANGE_PASSWORD: 'Đổi mật khẩu',
  CONTROL_OUTPUT: 'Bật/tắt ngõ ra',
  CONTROL_ROUTE: 'Điều khiển theo tuyến',
  RELEASE_CONTROL: 'Trả quyền về tự động',
  SYNC_RTC: 'Đồng bộ đồng hồ tủ',
  SAVE_SCHEDULE_DRAFT: 'Lưu nháp lịch',
  APPLY_SCHEDULE: 'Đẩy lịch xuống tủ',
  ROLLBACK_SCHEDULE: 'Lùi về bản lịch cũ',
  CREATE_STRATEGY: 'Tạo chiến lược',
  UPDATE_STRATEGY: 'Sửa chiến lược',
  DELETE_STRATEGY: 'Xoá chiến lược',
  APPLY_STRATEGY: 'Áp chiến lược',
  CREATE_PROJECT: 'Tạo dự án',
  CREATE_ROUTE: 'Tạo tuyến đường',
  UPDATE_ROUTE: 'Sửa tuyến đường',
  DELETE_ROUTE: 'Xoá tuyến đường',
  CREATE_CABINET: 'Tạo tủ',
  UPDATE_CABINET: 'Sửa tủ',
  DELETE_CABINET: 'Xoá tủ',
  UPDATE_OUTPUT: 'Sửa cấu hình ngõ ra',
  CREATE_USER: 'Tạo tài khoản',
  UPDATE_USER: 'Sửa tài khoản',
  DELETE_USER: 'Xoá tài khoản',
  SET_ROUTE_ACCESS: 'Phân quyền theo tuyến',
  ACK_ALARM: 'Ghi nhận cảnh báo',
};

const QUICK_FILTERS = [
  { value: '', label: 'Tất cả' },
  { value: 'CONTROL_OUTPUT', label: 'Bật/tắt ngõ ra' },
  { value: 'CONTROL_ROUTE', label: 'Điều khiển theo tuyến' },
  { value: 'APPLY_SCHEDULE', label: 'Đẩy lịch' },
  { value: 'APPLY_STRATEGY', label: 'Áp chiến lược' },
  { value: 'LOGIN', label: 'Đăng nhập' },
];

/**
 * ═══════════════════════════════════════════════════════════════════════════════════════
 * BA CẤP — bảng xếp cấp sống ở MÁY CHỦ (`services/capthaotac.ts`), đây chỉ vẽ và gửi bộ lọc
 * xuống. Giao diện KHÔNG giữ một bản sao của bảng: hai bản của một bảng là hai bản sẽ trôi
 * khỏi nhau (L67).
 *
 * ⚠ Nhóm thứ TƯ "chưa xếp" có chủ ý: một mã do bản máy chủ mới hơn ghi ra mà bảng chưa biết.
 *   Không có nhóm ấy thì nó không lọt bộ lọc nào và trở thành một dòng vô hình.
 */
const NUT_CAP: { gt: string; nhan: string; mau: string }[] = [
  { gt: '', nhan: 'Tất cả', mau: 'text-chu-2' },
  { gt: '1', nhan: 'Đụng tới đèn ngay', mau: 'text-nguy' },
  { gt: '2', nhan: 'Đổi cấu hình', mau: 'text-canh' },
  { gt: '3', nhan: 'Ra vào · tài khoản', mau: 'text-chu-2' },
  { gt: 'chua-xep', nhan: 'Chưa xếp cấp', mau: 'text-chua-ro' },
];

export default function AuditPage() {
  /*
   * ➕ `GD-08` 11/09/2026 — NHẬN BỘ LỌC TỪ ĐƯỜNG DẪN (`/nhat-ky?action=APPLY_STRATEGY`).
   *
   * 🔴 Trang Chiến lược có một liên kết *"Xem tất cả →"*, và phiếu ghi rõ nó phải mở trang này
   *   **đã lọc sẵn**. Không có đoạn này thì liên kết ấy mở ra toàn bộ nhật ký, và người bấm
   *   phải tự đi tìm lại đúng thứ họ vừa nhìn thấy — một lời hứa của giao diện mà trang đích
   *   không giữ. Cùng hạng lỗi với "ba câu trỏ vào một cái tên đã chết" (`GD-06`).
   * ⚠ CHỈ LÀ GIÁ TRỊ BAN ĐẦU (`useState(() => …)`), không phải một cái khoá: người dùng đổi ô
   *   lọc ngay sau đó là quyền của họ, và đường dẫn không được giành lại.
   * ⛔ Và KHÔNG nhận bừa một chuỗi từ URL: chỉ nhận mã có THẬT trong bảng nhãn đang dùng. Một
   *   `?action=` gõ sai sẽ lọc ra một danh sách rỗng, và cái rỗng ấy đọc thành "chưa ai làm
   *   việc này bao giờ" — một câu SAI (Luật số 0).
   */
  const [tsUrl] = useSearchParams();
  const [action, setAction] = useState(() => {
    const a = tsUrl.get('action') ?? '';
    return a && a in ACTION_LABEL ? a : '';
  });
  const [keyword, setKeyword] = useState('');
  const [cap, setCap] = useState('');
  /*
   * ⛔ THU GỌN MẶC ĐỊNH (lee 05/09). An toàn vì nhật ký thao tác là trang người ta CHỦ ĐỘNG
   *   mở để đi tra — khác hẳn dải cảnh báo, thứ phải đập vào mắt mà không ai xin (L52).
   *   Thu gọn vẫn phải nói ra CON SỐ, nếu không nó thành giấu.
   */
  const [mo, setMo] = useState(false);
  /*
   * 🔴 MỘT CÂU HỎI, MỘT CHỖ TRẢ LỜI: có tick thì xin cả nhật ký, không có thì xin `cuaToi=1`.
   * ⚠ `cuaToi` đi vào KHOÁ TRUY VẤN của react-query (xem `queries.ts`) — không thì hai phạm vi
   *   dùng chung một ô nhớ, và đổi tài khoản trong cùng một tab sẽ bày dữ liệu của người trước.
   */
  const coTick = useQuyen('tao_tai_khoan');
  const chiCuaToi = !coTick;
  const audit = useAudit(action, 300, mo, cap, chiCuaToi);
  const dem = useAuditDem(true, chiCuaToi);

  useEffect(() => { document.title = 'Nhật ký thao tác — SDKD'; }, []);

  const rows = useMemo(() => {
    const kw = keyword.trim().toLowerCase();
    const list = audit.data ?? [];
    if (!kw) return list;
    return list.filter((r) =>
      `${r.user?.username ?? ''} ${r.user?.fullName ?? ''} ${r.action} ${r.target ?? ''} ${r.ip ?? ''}`
        .toLowerCase()
        .includes(kw)
    );
  }, [audit.data, keyword]);

  return (
    <div className="space-y-4">
      <SectionTitle
        right={
          <Button
            size="sm"
            icon={<RefreshCw className="h-4 w-4" />}
            loading={audit.isFetching}
            onClick={() => void audit.refetch()}
          >
            Làm mới
          </Button>
        }
      >
        Nhật ký thao tác
      </SectionTitle>

      {/*
        * ⛔ NÓI RA PHẠM VI, và nói bằng ĐÚNG cái băng khoá dùng ở mọi chỗ khác (`components/Khoa`)
        *   — hổ phách, ổ khoá, và nêu tên người cấp quyền. Không tự vẽ một câu xám khác: ba nơi
        *   nói về cùng một tình huống bằng ba kiểu là ba nơi sẽ trôi khỏi nhau (mục `[95]`).
        */}
      {chiCuaToi && (
        <BangKhoa
          ten={NHAN_KHOA.tao_tai_khoan}
          cau="Bạn đang xem nhật ký thao tác CỦA CHÍNH BẠN — những việc do tài khoản này làm."
        />
      )}

      <Card
        title="Bộ lọc"
        bodyClass="p-3"
      >
        {/*
          🔴 BA NÚT CẤP ĐỘ (lee 05/09). Mỗi nút mang CON SỐ THẬT của cả nhật ký — số ấy do
             `/api/audit/dem` đếm trên toàn bảng, KHÔNG phải đếm trong 300 dòng đang tải.
             Một nút không có số thì phải bấm mới biết có gì; bấm rồi thấy rỗng thì không
             phân biệt được "không có" với "chưa tải tới" (L62).
        */}
        <div className="mb-2 flex flex-wrap items-center gap-2 border-b border-vien pb-2">
          {NUT_CAP.map((c) => (
            <Button
              key={c.gt}
              size="sm"
              variant={cap === c.gt ? 'primary' : 'secondary'}
              onClick={() => setCap(c.gt)}
            >
              <span className={cap === c.gt ? '' : c.mau}>{c.nhan}</span>
              {dem.data && (
                <span className="ml-1.5 text-xs opacity-70">
                  {c.gt === '' ? dem.data.tong ?? 0 : dem.data[c.gt] ?? 0}
                </span>
              )}
            </Button>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {QUICK_FILTERS.map((f) => (
            <Button
              key={f.value}
              size="sm"
              variant={action === f.value ? 'primary' : 'secondary'}
              onClick={() => setAction(f.value)}
            >
              {f.label}
            </Button>
          ))}
          <div className="relative ml-auto min-w-[14rem] flex-1">
            <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-chu-3" />
            <input
              className="inp pl-8"
              placeholder="Lọc theo người dùng, đối tượng, IP…"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
            />
          </div>
        </div>
      </Card>

      <Card
        title="Bản ghi gần nhất"
        goiY={{ nhan: 'Bản ghi gần nhất', noiDung: 'Giờ hiển thị theo múi giờ Việt Nam (GMT+7).' }}
        actions={
          <div className="flex items-center gap-2">
            {/*
              ⛔ THU GỌN VẪN PHẢI NÓI RA CON SỐ. Một khối thu gọn không mang số là một khối
                 GIẤU — người mở trang thấy một cái tiêu đề và không biết bên dưới có 3 dòng
                 hay 3000. Số ở đây là số THẬT của cả nhật ký (từ `/api/audit/dem`), không
                 phải số dòng đang tải; khi đang lọc thì nói rõ đang lọc cấp nào.
            */}
            <Badge tone="slate">
              {dem.data
                ? `${cap === '' ? dem.data.tong ?? 0 : dem.data[cap] ?? 0} bản ghi${cap ? ' ở cấp này' : ''}`
                : 'đang đếm…'}
            </Badge>
            <Button size="sm" variant="secondary" onClick={() => setMo((v) => !v)}>
              {mo ? 'Thu gọn' : 'Mở xem'}
            </Button>
          </div>
        }
      >
        {!mo && (
          <p className="px-1 py-2 text-sm text-chu-2">
            Khối đang thu gọn — bấm <b>Mở xem</b> để tải và đọc từng dòng.
            {cap && <> Bộ lọc đang bật: <b>{NUT_CAP.find((c) => c.gt === cap)?.nhan}</b>.</>}
          </p>
        )}
        {mo && audit.isLoading && <Spinner label="Đang tải nhật ký…" />}
        {mo && audit.isError && <ErrorBox error={audit.error} onRetry={() => void audit.refetch()} />}
        {mo && audit.data && rows.length === 0 && (
          <EmptyState
            icon={<ClipboardList className="h-8 w-8" />}
            title="Không có bản ghi nào khớp bộ lọc"
          />
        )}

        {mo && rows.length > 0 && (
          <div className="tbl-wrap cuon-doc">
            <table className="tbl tbl-cuon">
              <thead>
                <tr>
                  <th>Thời điểm</th>
                  <th>Người thực hiện</th>
                  <th>Hành động</th>
                  <th>Đối tượng</th>
                  <th>Chi tiết</th>
                  <th>IP</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id}>
                    <td className="whitespace-nowrap text-chu-2">
                      {fmtDateTime(r.at)}
                      <br />
                      <span className="text-xs text-chu-3">{fmtAgo(r.at)}</span>
                    </td>
                    <td className="whitespace-nowrap">
                      {r.user ? (
                        <>
                          <span className="font-medium text-chu-1">{r.user.fullName}</span>
                          <br />
                          <span className="text-xs text-chu-2">@{r.user.username}</span>
                        </>
                      ) : (
                        /*
                         * ⛔ KHÔNG CÒN MỘT CHỮ "hệ thống" CHO HAI VIỆC KHÁC NHAU (lee 07/09).
                         *   `userId = null` có HAI nghĩa, và gộp chúng là mất đúng thứ lee
                         *   vừa yêu cầu phân biệt:
                         *     · người bấm màn hình / gạt công tắc NGAY TẠI TỦ
                         *     · máy chủ tự làm
                         *   Để trống còn tệ hơn nữa — nó bị đọc thành "không ai làm".
                         */
                        <span className="text-chu-3">{aiDaLam(r.action, false)}</span>
                      )}
                    </td>
                    <td><Badge tone={actionTone(r.action)}>{ACTION_LABEL[r.action] ?? r.action}</Badge></td>
                    <td className="text-xs"><TargetLink target={r.target} /></td>
                    <td className="max-w-[24rem] text-xs text-chu-2">
                      <DetailCell detail={r.detail} />
                    </td>
                    <td className="whitespace-nowrap text-xs text-chu-3">{r.ip ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

function actionTone(action: string): 'red' | 'amber' | 'blue' | 'green' | 'slate' {
  if (action.startsWith('DELETE')) return 'red';
  if (action.startsWith('CONTROL') || action === 'RELEASE_CONTROL') return 'green';
  if (action.includes('SCHEDULE') || action.includes('STRATEGY')) return 'blue';
  if (action.startsWith('CREATE') || action.startsWith('UPDATE') || action.startsWith('SET')) return 'amber';
  return 'slate';
}

/** target có dạng "cabinet:<id>" | "route:<id>" | "user:<id>"… */
function TargetLink({ target }: { target: string | null }) {
  if (!target) return <span className="text-chu-3">—</span>;
  const [kind, id] = target.split(':');
  if (kind === 'cabinet' && id) {
    return <Link to={`/tu/${id}`} className="text-nhan hover:underline">Tủ {id.slice(0, 8)}…</Link>;
  }
  if (kind === 'route' && id) {
    return <Link to={`/dieu-khien?tuyen=${id}`} className="text-nhan hover:underline">Tuyến {id.slice(0, 8)}…</Link>;
  }
  return <span className="text-chu-2">{target}</span>;
}

function DetailCell({ detail }: { detail: AuditRow['detail'] }) {
  if (detail === null || detail === undefined) return <span className="text-chu-3">—</span>;

  if (typeof detail === 'object') {
    const d = detail as Record<string, unknown>;
    if (Array.isArray(d.outputs)) {
      return (
        <span>
          {(d.outputs as { index: number; on: boolean }[])
            .map((o) => `ngõ ${o.index}: ${o.on ? 'BẬT' : 'TẮT'}`)
            .join(', ')}
          {typeof d.count === 'number' ? ` · ${d.count} tủ` : ''}
        </span>
      );
    }
    return <span className="break-words">{JSON.stringify(detail)}</span>;
  }
  return <span>{String(detail)}</span>;
}
